'use client'

import type { ArchiveBlock as ArchiveBlockProps, Post } from '@/payload-types'

type PostWithImage = Post & {
  postImage?: Post['postImage']
}
import React, { useEffect, useState } from 'react'
import RichText from '@/components/RichText'
import BlockWrapper from '@/components/BlockWrapper'
import { Media } from '@/components/Media'

// Client-safe version of ArchiveBlock for use in RichText converters
export const ArchiveBlockClient: React.FC<ArchiveBlockProps & { id?: string }> = (props) => {
  const { title, description, categories, limit: limitFromProps, excludeCurrentPage = true } = props
  const [posts, setPosts] = useState<PostWithImage[]>([])
  const [loading, setLoading] = useState(true)

  const limit = limitFromProps || 10 // Match config default

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        if (categories && Array.isArray(categories) && categories.length > 0) {
          const allCategoryPosts: PostWithImage[] = []
          for (const category of categories) {
            const categoryId = typeof category === 'object' ? category.id : category
            if (categoryId) {
              const queryParams = new URLSearchParams({
                limit: limit.toString(),
                depth: '1',
                sort: '-publishedAt',
                'where[categories][in]': categoryId,
                'where[_status][equals]': 'published',
              })
              if (excludeCurrentPage) {
                const currentPath = window.location.pathname
                const pathSegments = currentPath.split('/').filter(Boolean)
                const potentialSlug = pathSegments[pathSegments.length - 1]?.split('?')[0]
                if (potentialSlug) {
                  queryParams.append('where[slug][not_equals]', potentialSlug)
                }
              }
              const apiUrl = `/api/posts?${queryParams.toString()}`
              const response = await fetch(apiUrl)
              const data = await response.json()
              if (data.docs) {
                allCategoryPosts.push(...data.docs)
              }
            }
          }
          const uniquePosts = allCategoryPosts.filter(
            (post, index, self) => index === self.findIndex((p) => p.id === post.id),
          )
          const toTime = (p: PostWithImage) =>
            new Date((p as any)?.publishedAt || (p as any)?.createdAt || 0).getTime()
          const sortedDesc = uniquePosts.sort((a, b) => toTime(b) - toTime(a))
          setPosts(sortedDesc.slice(0, limit))
        } else {
          const queryParams = new URLSearchParams({
            limit: limit.toString(),
            depth: '1',
            sort: '-publishedAt',
            'where[_status][equals]': 'published',
          })
          if (excludeCurrentPage) {
            const currentPath = window.location.pathname
            const pathSegments = currentPath.split('/').filter(Boolean)
            const potentialSlug = pathSegments[pathSegments.length - 1]?.split('?')[0]
            if (potentialSlug) {
              queryParams.append('where[slug][not_equals]', potentialSlug)
            }
          }
          const apiUrl = `/api/posts?${queryParams.toString()}`
          const response = await fetch(apiUrl)
          const data = await response.json()
          const toTime = (p: PostWithImage) =>
            new Date((p as any)?.publishedAt || (p as any)?.createdAt || 0).getTime()
          const docs: PostWithImage[] = Array.isArray(data?.docs) ? data.docs : []
          setPosts(docs.sort((a, b) => toTime(b) - toTime(a)))
        }
      } catch (error) {
        console.error('Error fetching posts:', error)
        setPosts([])
      } finally {
        setLoading(false)
      }
    }
    fetchPosts()
  }, [categories, limit, excludeCurrentPage])

  if (loading) {
    return (
      <BlockWrapper>
        {title && (
          <RichText
            className="container mb-6 mx-auto text-center text-2xl md:text-3xl lg:text-4xl font-bold lg:max-w-3xl text-foreground"
            data={title}
            enableGutter={false}
            enableProse={false}
          />
        )}
        {description && (
          <RichText
            className="container mb-8 mx-auto text-center text-lg md:text-xl lg:max-w-2xl text-muted-foreground"
            data={description}
            enableGutter={false}
            enableProse={false}
          />
        )}
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: limit }).map((_, index) => (
              <div key={index} className="animate-pulse">
                <div className="bg-muted aspect-video rounded-xl mb-4"></div>
                <div className="space-y-2">
                  <div className="bg-muted h-4 rounded w-3/4"></div>
                  <div className="bg-muted h-6 rounded w-full"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </BlockWrapper>
    )
  }

  return (
    <BlockWrapper>
      {title && (
        <RichText
          className="container mb-6 mx-auto text-center text-2xl md:text-3xl lg:text-4xl font-bold lg:max-w-3xl text-foreground"
          data={title}
          enableGutter={false}
          enableProse={false}
        />
      )}

      {description && (
        <RichText
          className="container mb-8 mx-auto text-center text-lg md:text-xl lg:max-w-2xl text-muted-foreground"
          data={description}
          enableGutter={false}
          enableProse={false}
        />
      )}

      <div className="container">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts?.map((post, index) => {
            if (typeof post === 'object' && post !== null) {
              return (
                <article key={post.id || index} className="group">
                  {/* Image Section */}
                  {post.postImage ? (
                    <div className="relative w-full mb-4 overflow-hidden rounded-xl flex items-center justify-center">
                      <Media
                        resource={post.postImage}
                        size="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                        className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-105"
                        fetchPriority="low"
                        loading="lazy"
                      />
                    </div>
                  ) : null}

                  {/* Post Content */}
                  <div className="space-y-2">
                    {/* Categories */}
                    {post.categories &&
                      Array.isArray(post.categories) &&
                      post.categories.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {post.categories.map((category, catIndex) => {
                            if (typeof category === 'object' && category.title) {
                              return (
                                <span
                                  key={catIndex}
                                  className="text-xs uppercase tracking-wide text-muted-foreground bg-muted px-2 py-1 rounded-md"
                                >
                                  {category.title}
                                </span>
                              )
                            }
                            return null
                          })}
                        </div>
                      )}

                    {/* Title */}
                    {post.title && (
                      <h3 className="text-xl font-bold leading-tight text-foreground group-hover:text-primary transition-colors">
                        <a
                          href={(() => {
                            // Get the first category for URL construction
                            const firstCategory = Array.isArray(post.categories)
                              ? post.categories[0]
                              : post.categories

                            // If category exists and has slug, use /category-slug/post-slug format
                            if (
                              firstCategory &&
                              typeof firstCategory === 'object' &&
                              firstCategory.slug
                            ) {
                              return `/${firstCategory.slug}/${post.slug}`
                            }

                            // Fallback to /post-slug if no category
                            return `/${post.slug}`
                          })()}
                          className="block"
                        >
                          {post.title}
                        </a>
                      </h3>
                    )}
                  </div>
                </article>
              )
            }
            return null
          })}
        </div>
      </div>
    </BlockWrapper>
  )
}
